import { ARTICLES } from "./insights";
import { pick, type ContentLang } from "@/lib/locale";

export type InsightLink = {
  href: string;
  title: string;
  topic: string;
  summary: string;
};

const SALARY: Record<ContentLang, Omit<InsightLink, "href">> = {
  pl: {
    title: "Przewodnik po wynagrodzeniach w IT: Polska 2026",
    topic: "Wynagrodzenia",
    summary: "Widełki UoP i B2B, Kraków kontra Warszawa oraz realny koszt zatrudnienia.",
  },
  en: {
    title: "IT salary guide: Poland 2026",
    topic: "Compensation",
    summary: "UoP and B2B benchmarks, Kraków vs. Warsaw and the true cost of hiring.",
  },
  zh: {
    title: "波兰IT薪酬指南: 2026",
    topic: "薪酬水平",
    summary: "劳动合同与B2B薪酬区间, Krakow与华沙对比，以及真实用工成本。",
  },
};

const RPO_SERVICE: Record<ContentLang, Omit<InsightLink, "href">> = {
  pl: {
    title: "IT RPO Polska: outsourcing procesu rekrutacji",
    topic: "Usługi",
    summary: "Wbudowany proces rekrutacji tech dla firm skalujących zespoły w Polsce i CEE.",
  },
  en: {
    title: "IT RPO Poland: recruitment process outsourcing",
    topic: "Services",
    summary: "Embedded tech recruitment for companies scaling teams in Poland and CEE.",
  },
  zh: {
    title: "波兰IT RPO: 招聘流程外包",
    topic: "服务",
    summary: "为在波兰及中东欧扩张团队的企业提供嵌入式技术招聘流程。",
  },
};

/**
 * Kolejność wg potencjału SEO (wolumen + intencja komercyjna).
 * Pierwszy wpis jest prezentowany jako wyróżniony, kolejne dwa jako poboczne.
 */
const SEO_PRIORITY = [
  "/insights/it-salary-guide-poland",
  "/insights/rekrutacja-it-krakow",
  "/insights/rekrutacja-it-warszawa",
  "/insights/koszt-rekrutacji-it-polska",
  "/insights/b2b-czy-uop-zatrudnianie-programistow",
  "/insights/employer-of-record-polska",
  "/insights/headhunting-it-direct-search",
  "/insights/it-outsourcing-nearshoring-polska",
  "/insights/rekrutacja-devops-cloud",
  "/insights/ai-w-rekrutacji-it",
  "/insights/rekrutacja-ai-ml-polska",
  "/insights/rekrutacja-data-engineering",
  "/insights/rekrutacja-it-cee",
  "/services/it-rpo-poland",
];

/** Flat, crawlable list of every Insights entry - used in the on-page link index. */
export function insightLinks(lang: ContentLang): InsightLink[] {
  const all: InsightLink[] = [
    { href: "/insights/it-salary-guide-poland", ...SALARY[lang] },
    ...ARTICLES.map((a) => ({
      href: `/insights/${a.slug}`,
      title: a[lang].title,
      topic: pick(a.topic, lang),
      summary: a[lang].summary,
    })),
    { href: "/services/it-rpo-poland", ...RPO_SERVICE[lang] },
  ];

  const rank = (href: string) => {
    const i = SEO_PRIORITY.indexOf(href);
    return i === -1 ? SEO_PRIORITY.length : i;
  };

  return all.sort((a, b) => rank(a.href) - rank(b.href));
}

export const INDEX_COPY: Record<
  ContentLang,
  { eyebrow: string; title: string; all: string; more: (n: number) => string; less: string }
> = {
  pl: {
    eyebrow: "Insights",
    title: "Wszystkie przewodniki",
    all: "Zobacz sekcję Insights →",
    more: (n) => `Rozwiń pozostałe przewodniki (${n})`,
    less: "Zwiń listę przewodników",
  },
  en: {
    eyebrow: "Insights",
    title: "All guides",
    all: "Go to Insights →",
    more: (n) => `Show remaining guides (${n})`,
    less: "Collapse guide list",
  },
  zh: {
    eyebrow: "Insights",
    title: "全部指南",
    all: "前往 Insights →",
    more: (n) => `展开其余指南 (${n})`,
    less: "收起指南列表",
  },
};

